import React, {useState} from 'react'
import {Trans, useTranslation} from 'react-i18next'

import {
    ActionButton,
    DefaultButton,
    IconButton,
    IIconProps,
    Modal,
    PrimaryButton,
    Separator,
    Spinner,
    SpinnerSize,
    Stack,
    StackItem,
    Text,
    TextField
} from "@fluentui/react"

import {contentStyles, iconButtonStyles} from "./Style"
import StatusMessage, {StatusEnum} from "../../share/StatusMessage"
import {useBranding} from '../../hooks/useBranding'
import ApprovalService, {IGetApprovalListMasterResponse, IPostApprovalListDetailRequest} from "../../services/Approval"
import {decode} from 'base64-arraybuffer'
import {decode as decodeHtml} from 'html-entities'


type Properties = {
    showModal: boolean
    onDismissModal: (update: boolean) => void;
    item: IGetApprovalListMasterResponse | undefined
    documentOnlyAudienceId: string
}

export function ApprovalListUpload({
                                       documentOnlyAudienceId,
                                       item,
                                       onDismissModal,
                                       showModal,
                                   }: Properties) {

    const {t} = useTranslation(['approval', 'common'])
    const {branding} = useBranding()
    const cancelIcon: IIconProps = {iconName: 'Cancel'}
    const [isSaving, setIsSaving] = useState(false)
    const [message, setMessage] = useState("")
    const [showMessage, setShowMessage] = useState(false)
    const [statusMessageEnum, setStatusMessageEnum] = useState<StatusEnum>(StatusEnum.Info)
    const [fileName, setFileName] = useState("")
    const instructionItems: any = t('text.instructionUpload', {ns: 'approval', returnObjects: true})
    const [uploadingFile, setUploadingFile] = useState(false)
    const [requiredUploadFile, setRequiredUploadFile] = useState(false)
    const [base64File, setBase64File] = useState<string>("")


    const upload = async () => {

        setShowMessage(false)
        setIsSaving(true)


        const requestKIM: IPostApprovalListDetailRequest = {
            Base64File: base64File,
            ApprovalListMasterId: item?.Id.toString() ?? ''
        }

        await ApprovalService.postApprovalListDetail(requestKIM)
            .then(data => {
                if (data.Success) {
                    onDismissModal(true)
                } else {
                    ShowStatusMessage(t('message.error', {
                        ns: 'common',
                        Error: data.ExceptionMessage
                    }), StatusEnum.Error)
                }
            })
            .catch((error) => {
                ShowStatusMessage(t('message.error', {ns: 'common', Error: error}), StatusEnum.Error)
            })
            .finally(() => {
                setIsSaving(false)
            })
    }

    /**
     * Displays a status message with the specified content and status type.
     *
     * @function ShowStatusMessage
     * @param {string} message - The message text to be displayed.
     * @param {StatusEnum} status - An enumeration representing the type or status of the message (e.g., success, error).
     * @returns {void}
     */
    const ShowStatusMessage = (message: string, status: StatusEnum): void => {
        setMessage(decodeHtml(message))
        setStatusMessageEnum(status)
        setShowMessage(true)
    }


    const downloadExample = async () => {
        const name = "Example.csv"
        const Base64 = {blob: "77u/Q29sdW1uMSxDb2x1bW4yLENvbHVtbjMsQ29sdW1uNCxDb2x1bW41LENvbHVtbjYKMSwsLCwsCjIsLCwsLAozLCwsLCw="}
        const decoded = decode(Base64.blob)
        try {
            const file = new Blob(
                [decoded], {type: "application/vnd.ms-excel"})
            const a = document.createElement("a")
            a.href = window.URL.createObjectURL(file)
            a.download = name
            a.click()


            ShowStatusMessage(t('message.successDownload', {
                ns: 'common'
            }), StatusEnum.Success)

            setTimeout(function () {
                setShowMessage(false)
            }, 3000)
        } catch (error) {
            console.log(error)
        }
    }

    const getBase64 = (file: File) => {
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = function () {
            setBase64File(reader.result!.toString().split(',')[1])
        }
        reader.onloadend = () => {
            setRequiredUploadFile(true)
        }
    }

    const uploadFile = React.useCallback((ev: React.ChangeEvent<HTMLInputElement>) => {
        setRequiredUploadFile(false)
        setFileName('')
        setUploadingFile(true)

        if (ev.target.files != null && ev.target.files.length > 0) {
            setShowMessage(false)
            const mimeType = ev.target.files![0].type

            if (mimeType.indexOf("vnd.ms-excel") >= 0 || mimeType.indexOf(".txt") > 0 || mimeType.indexOf("text/csv") >= 0) {
                const name = ev.target.files![0].name
                setRequiredUploadFile(true)
                setFileName(name)
                getBase64(ev.target.files![0])
            } else {
                ShowStatusMessage("This type of file is not valid. Must be a CSV with a .csv extenstion.", StatusEnum.Error)
            }
        } else {
            ShowStatusMessage("File not found", StatusEnum.Error)
        }
        setUploadingFile(false)
    }, [getBase64, ShowStatusMessage, setFileName, setRequiredUploadFile, setShowMessage, setUploadingFile])


    return (
        <>
            <Modal
                isOpen={showModal}
                isBlocking={true}
                containerClassName={contentStyles(branding.theme).containerUpload}
            >

                <Stack grow={1} styles={{root: {height: "100%"}}}>
                    <Stack tokens={{childrenGap: 8}} styles={{
                        root: {
                            height: 56,
                            alignItems: 'center'
                        }
                    }} horizontal>

                        <Stack grow={1}
                               styles={{
                                   root: {
                                       textOverflow: "ellipsis",
                                       paddingLeft: 16,
                                       whiteSpace: "nowrap",
                                       overflow: "hidden"
                                   }
                               }}>
                            <Text variant={"xLarge"} id={'approvalStepEditTitle'}
                                  title={t('title.uploadCSV', {ns: 'approval'})}>
                                {t('title.uploadCSV', {ns: 'approval'})}
                            </Text>
                        </Stack>

                        <Stack horizontal styles={{root: {alignItems: "center", paddingRight: 8}}}>
                            <IconButton
                                styles={iconButtonStyles}
                                iconProps={cancelIcon}
                                ariaLabel={t('Common.Button.Close', {ns: 'common'})}
                                onClick={() => {
                                    onDismissModal(false)
                                }}
                                title={t('Common.Button.Close', {ns: 'common'})}
                            />
                        </Stack>
                    </Stack>
                    <Stack grow={1} styles={{root: {height: 1}}}>

                        <Stack horizontal styles={{root: {height: '100%'}}}>

                            <Stack grow={1} styles={{root: {height: '100%', width: 1}}}>

                                {/*-- START CONTENT --*/}
                                <Stack grow={1} styles={{root: {overflowY: 'auto', paddingLeft: 16, paddingRight: 16}}}
                                       className="scrollVisible">


                                    {showMessage &&
                                        <>
                                            <StackItem styles={{root: {marginBottom: 16}}}>

                                                <StatusMessage status={statusMessageEnum} hasTimer={false}
                                                               truncated={true}
                                                               isMultiline={true}
                                                               dismiss={true}
                                                               setShowMessage={setShowMessage}>
                                                    <div>{message}</div>
                                                </StatusMessage>
                                            </StackItem>

                                        </>
                                    }

                                    <ActionButton
                                        title={t('title.exampleCSVList', {ns: 'approval'})}
                                        text={t('text.exampleCSVList', {ns: 'approval'})}
                                        styles={{
                                            root: {
                                                color: branding.theme.palette.black,
                                                marginLeft: 'auto',
                                                marginTop: '4px'
                                            },

                                            icon: {
                                                color: branding.theme.palette.greenDark,
                                                fontSize: '20px'
                                            },
                                            iconHovered: {
                                                color: branding.theme.palette.greenDark
                                            }
                                        }}
                                        onClick={downloadExample}
                                        iconProps={{iconName: 'ExcelDocument'}}/>


                                    <Text variant={'medium'} styles={{root: {paddingTop: 8, paddingBottom: 8}}}>
                                        <Trans>
                                            <ul>
                                                {instructionItems.map((item: string, index: number) => (
                                                    <li key={index}>{item}</li>
                                                ))}
                                            </ul>
                                        </Trans>

                                    </Text>

                                    <StackItem styles={{root: {paddingTop: 8}}}>
                                        <Stack>
                                            <StackItem grow={1}>


                                                <Stack horizontal>
                                                    <Stack>
                                                        <label htmlFor="file">
                                                            <div className="divHover divToolBoxItem" style={{
                                                                color: branding.theme.palette.white,
                                                                backgroundColor: branding.theme.palette.themePrimary,
                                                                padding: 8
                                                            }}>Choose File
                                                            </div>
                                                        </label>
                                                        <input accept=".csv" className="fileButton" id="file"
                                                               type="file" name="file" onChange={uploadFile}
                                                               placeholder={t('placeholder.file', {ns: 'common'})}/>
                                                    </Stack>
                                                    <Stack grow={1}>
                                                        <TextField key="tesxtFilename" disabled value={fileName}
                                                                   title={t('title.file', {ns: 'common'})}/>
                                                    </Stack>
                                                </Stack>


                                            </StackItem>
                                        </Stack>
                                    </StackItem>


                                </Stack>
                                {/*-- END CONTENT --*/}

                                {/*-- START FOOTER --*/}
                                <Separator></Separator>


                                <Stack horizontal tokens={{childrenGap: 8}}
                                       styles={{
                                           root: {
                                               paddingRight: 16,
                                               paddingBottom: 12,
                                               alignItems: 'center'
                                           }
                                       }}>
                                    <Stack grow={1}>&nbsp;</Stack>

                                    {isSaving &&
                                        <>
                                            <StackItem>
                                                <Spinner size={SpinnerSize.medium}
                                                         labelPosition="left"
                                                         label={t('text.spinnerUploading', {ns: 'common'})}/>
                                            </StackItem>
                                        </>
                                    }

                                    <StackItem>


                                        <PrimaryButton disabled={isSaving || !requiredUploadFile || uploadingFile}
                                                       style={{marginLeft: 8}}
                                                       onClick={upload}
                                                       text={t('Common.Button.Upload', {ns: 'common'})}
                                                       title={t('Common.Button.Upload', {ns: 'common'})}/>


                                        <DefaultButton
                                            onClick={() => {
                                                onDismissModal(false)
                                            }}
                                            style={{marginLeft: 8}}
                                            text={t('Common.Button.Close', {ns: 'common'})}
                                            title={t('Common.Button.Close', {ns: 'common'})}/>

                                    </StackItem>
                                </Stack>

                                {/*-- END FOOTER --*/}

                            </Stack>
                        </Stack>
                    </Stack>
                </Stack>
            </Modal>

        </>
    )
}
